/* ---------------------------------------------------------------------------
   Palpa How To — drives the stepped walkthrough.

   The markup already ships a valid tablist with the first step selected, so
   this script only ever changes state: `data-state` on a step, `is-active` on
   a panel, `--pht-progress` on the section. Nothing here builds DOM, which is
   what lets the section render correctly before the script arrives.

   Two ways to advance, both merchant-set:
     • "clip"  — the progress ring tracks the video's own playhead and the step
                 changes when the clip ends. The pacing is the footage's.
     • "timer" — a fixed number of seconds per step; the clip loops underneath.

   Playback is deliberately conservative: clips are `preload="none"` until they
   are needed, only the current one plays, and everything stops the moment the
   section leaves the viewport or the tab goes to the background. A visitor who
   scrolls past never pays for the video.
   --------------------------------------------------------------------------- */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HowTo {
    constructor(root) {
      this.root = root;
      this.steps = [...root.querySelectorAll('.pht__step')];
      this.panels = [...root.querySelectorAll('.pht__panel')];
      this.dots = [...root.querySelectorAll('.pht__dot, .pht__pip')];
      // Every listener below is filed under this one signal, so tearing the
      // instance down is a single call — see `destroy`.
      this.abort = new AbortController();
      const { signal } = this.abort;
      if (this.steps.length === 0) return;

      this.mode = root.dataset.advance === 'timer' ? 'timer' : 'clip';
      this.interval = (parseFloat(root.dataset.interval) || 5) * 1000;
      this.autoplay = root.dataset.autoplay === 'true' && !reduceMotion.matches;

      this.index = 0;
      this.visible = false;
      this.frame = null;
      this.startedAt = 0;
      this.leaveTimers = new Map();

      this.steps.forEach((step, i) => {
        step.addEventListener('click', () => this.select(i, { focus: false }), { signal });
        step.addEventListener('keydown', (event) => this.onKeydown(event, i), { signal });
      });

      // Shown one step at a time, the arrows and their dots are the only way
      // past a clip the visitor does not want to sit through.
      root.querySelectorAll('.pht__arrow').forEach((arrow) => {
        const step = Number(arrow.dataset.step) || 1;
        arrow.addEventListener('click', () => this.shift(step), { signal });
      });

      root.querySelectorAll('.pht__pip').forEach((pip) => {
        pip.addEventListener('click', () => this.select(Number(pip.dataset.index), { focus: false }), { signal });
      });

      this.paint(0);
      this.watch();

      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) this.pause();
          else if (this.visible) this.play();
        },
        { signal }
      );
    }

    /* The theme editor swaps this section's DOM out from under us on every
       edit. Without this, each swap would leave an orphan behind: an animation
       frame still ticking against a detached node and a document-level
       listener that outlives the element it was written for. */
    destroy() {
      this.pause();
      this.abort.abort();
      this.observer?.disconnect();
      this.leaveTimers?.forEach((timer) => clearTimeout(timer));
    }

    video(i) {
      const panel = this.panels[i];
      return panel ? panel.querySelector('video') : null;
    }

    /* Nothing is fetched until the section is actually on screen, so a visitor
       who never scrolls this far pays nothing. Once it is: the current clip is
       worth its bytes, the next one is worth its metadata so the swap does not
       land on an empty frame, and the rest stay untouched.

       With autoplay off — reduced motion, or the merchant's choice — the
       current clip only loads metadata, which is enough for its first frame to
       paint as a still. */
    prime() {
      const wantedFor = (i) => {
        if (!this.visible) return 'none';
        if (i === this.index) return this.autoplay ? 'auto' : 'metadata';
        if (i === (this.index + 1) % this.panels.length) return 'metadata';
        return 'none';
      };

      this.panels.forEach((panel, i) => {
        const video = panel.querySelector('video');
        if (!video) return;
        const wanted = wantedFor(i);
        if (video.getAttribute('preload') !== wanted) video.setAttribute('preload', wanted);
        if (wanted !== 'none' && video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
      });
    }

    paint(next) {
      const previous = this.index;
      this.index = next;

      this.steps.forEach((step, i) => {
        const state = i === next ? 'active' : i < next ? 'done' : 'todo';
        step.dataset.state = state;
        step.setAttribute('aria-selected', String(i === next));
        step.tabIndex = i === next ? 0 : -1;
      });

      this.dots.forEach((dot) => dot.classList.toggle('is-active', Number(dot.dataset.index) === next));

      this.panels.forEach((panel, i) => {
        if (i === next) {
          // Clear a pending exit: the visitor can come back before it finishes.
          clearTimeout(this.leaveTimers.get(i));
          this.leaveTimers.delete(i);
          panel.classList.remove('is-leaving');
          panel.classList.add('is-active');
        } else if (panel.classList.contains('is-active')) {
          panel.classList.remove('is-active');
          panel.classList.add('is-leaving');
          const timer = setTimeout(() => panel.classList.remove('is-leaving'), 600);
          this.leaveTimers.set(i, timer);
        }
      });

      if (previous !== next) {
        const leaving = this.video(previous);
        if (leaving) {
          leaving.pause();
          leaving.currentTime = 0;
        }
      }

      this.prime();
      this.root.style.setProperty('--pht-progress', '0');
      this.startedAt = performance.now();

      const current = this.video(next);
      if (current) {
        current.muted = true;
        current.loop = this.mode === 'timer';
        if (this.visible && this.autoplay) current.play().catch(() => {});
      }
    }

    select(i, { focus = true } = {}) {
      if (i === this.index) return;
      this.paint(i);
      if (focus) this.steps[i].focus();
      if (this.visible && this.autoplay) this.play();
    }

    next() {
      this.paint((this.index + 1) % this.steps.length);
    }

    /* Move by a number of steps, wrapping both ways: the arrows never dead-end. */
    shift(by) {
      const count = this.steps.length;
      this.select((this.index + by + count) % count, { focus: false });
    }

    onKeydown(event, i) {
      const last = this.steps.length - 1;
      let target = null;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          target = i === last ? 0 : i + 1;
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          target = i === 0 ? last : i - 1;
          break;
        case 'Home':
          target = 0;
          break;
        case 'End':
          target = last;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.select(target);
    }

    tick() {
      this.frame = requestAnimationFrame(() => this.tick());

      const video = this.video(this.index);
      const clipTimed = this.mode === 'clip' && video && Number.isFinite(video.duration) && video.duration > 0;

      if (clipTimed) {
        this.root.style.setProperty('--pht-progress', String(Math.min(video.currentTime / video.duration, 1)));
        // `ended` is unreliable when a clip is swapped mid-play, so the playhead
        // itself is the source of truth.
        if (video.currentTime >= video.duration - 0.05) this.next();
        return;
      }

      const elapsed = performance.now() - this.startedAt;
      this.root.style.setProperty('--pht-progress', String(Math.min(elapsed / this.interval, 1)));
      if (elapsed >= this.interval) this.next();
    }

    play() {
      if (!this.autoplay) return;
      const video = this.video(this.index);
      if (video) {
        video.muted = true;
        video.play().catch(() => {});
      }
      if (this.frame) return;
      this.startedAt = performance.now() - this.interval * (parseFloat(this.root.style.getPropertyValue('--pht-progress')) || 0);
      this.frame = requestAnimationFrame(() => this.tick());
    }

    pause() {
      cancelAnimationFrame(this.frame);
      this.frame = null;
      const video = this.video(this.index);
      if (video) video.pause();
    }

    watch() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.visible = entry.isIntersecting;
            if (this.visible) {
              this.prime();
              this.play();
            } else {
              this.pause();
            }
          });
        },
        { threshold: 0.35 }
      );

      this.observer.observe(this.root);
    }
  }

  const live = new Set();

  const init = (scope) => {
    scope.querySelectorAll('[data-palpa-how-to]').forEach((root) => live.add(new HowTo(root)));
  };

  const teardown = (scope) => {
    live.forEach((instance) => {
      if (!scope.contains(instance.root)) return;
      instance.destroy();
      live.delete(instance);
    });
  };

  const find = (node) => {
    const root = node.closest('[data-palpa-how-to]');
    return [...live].find((instance) => instance.root === root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);

  /* Theme editor. Adding, removing or reordering a step re-renders the whole
     section, so the old instance has to be let go before the new one starts. */
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  /* Shown one step at a time, a merchant editing step four would otherwise be
     editing something they cannot see. Selecting the block brings it forward
     and holds it there — the rotation would only carry it away again. */
  document.addEventListener('shopify:block:select', (event) => {
    const step = event.target.closest('.pht__step');
    if (!step) return;
    const instance = find(step);
    if (!instance) return;
    instance.select(Number(step.dataset.index), { focus: false });
    instance.pause();
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const instance = event.target.closest('.pht__step') && find(event.target);
    if (instance && instance.visible) instance.play();
  });
})();
